import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Project } from '../types';
import './Portfolio.css';

interface Category {
  id: string;
  label: string;
}

const Portfolio: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const categories: Category[] = [
    { id: 'all', label: 'All' },
    { id: 'Blockchain', label: 'Blockchain' },
    { id: 'Backend', label: 'Backend' },
    { id: 'Full Stack', label: 'Full Stack' },
    { id: 'Frontend', label: 'Frontend' }
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    filterProjects();
  }, [projects, activeCategory]);

  const fetchProjects = async (): Promise<void> => {
    try {
      // Mock data for development
      const mockProjects: Project[] = [
        {
          id: '1',
          title: 'Jetton Tokenomics Platform',
          description: 'Implemented tokenomics in Telegram group management program (Gatekeeper) using TON blockchain. Designed FT-based Jetton token with custom random logic for lottery features.',
          image: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=600',
          category: 'Blockchain',
          tags: ['TON', 'Jetton', 'Tact', 'TypeScript']
        },
        {
          id: '2',
          title: 'AI Chatbot Admin System',
          description: 'Backend development for Kakao Work chatbot admin page with document management. Improved RAG-based chatbot with OCR-based user input processing.',
          image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600',
          category: 'Backend',
          tags: ['Spring Boot', 'RAG', 'OCR', 'AI']
        },
        {
          id: '3',
          title: 'Real Estate Platform',
          description: 'OCR-based Jeonse fraud detection and real estate information platform. Built stable cloud infrastructure with ELK stack and Kakao Cloud architecture.',
          image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600',
          category: 'Full Stack',
          tags: ['Spring', 'Docker', 'ELK', 'Kakao Cloud']
        },
        {
          id: '4',
          title: 'GDGOC Community Website',
          description: 'Built member management and authentication processes for GDGoC Gachon community website using Spring Scheduler for automatic member status updates.',
          image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600',
          category: 'Backend',
          tags: ['Spring', 'Authentication', 'Community']
        },
        {
          id: '5',
          title: 'TON Community Management',
          description: 'Managed Telegram communities with 7,000+ members during KBW2024. Organized meme competitions, AMAs, and community events.',
          image: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=600',
          category: 'Frontend',
          tags: ['Community', 'Event Management', 'TON']
        },
        {
          id: '6',
          title: 'Silta - AI Copyright Protection',
          description: 'Platform protecting generative AI image copyrights using IPFS and blockchain for decentralized ownership recording. Won Excellence Award in AI & Blockchain Competition.',
          image: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=600',
          category: 'Blockchain',
          tags: ['IPFS', 'AI', 'Copyright', 'Blockchain']
        }
      ];
      setProjects(mockProjects);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterProjects = (): void => {
    if (activeCategory === 'all') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(projects.filter(project => project.category === activeCategory));
    }
  };

  const openLightbox = (project: Project): void => {
    setSelectedProject(project);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = (): void => {
    setSelectedProject(null);
    document.body.style.overflow = 'unset';
  };

  if (loading) {
    return (
      <div className="portfolio-loading">
        <div className="loading-spinner"></div>
        <p>Loading portfolio...</p>
      </div>
    );
  }

  return (
    <div className="portfolio">
      <div className="portfolio-header">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>Portfolio</h1>
            <p>A collection of my blockchain and full-stack development projects</p>
          </motion.div>
        </div>
      </div>

      <div className="portfolio-content">
        <div className="container">
          {/* Category Filter */}
          <div className="category-filter">
            {categories.map((category) => (
              <button
                key={category.id}
                className={`filter-btn ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* Project Grid */}
          <motion.div 
            className="project-grid"
            layout
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                className="project-item"
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                onClick={() => openLightbox(project)}
              >
                <div className="project-wrapper">
                  <img src={project.image} alt={project.title} />
                  <div className="project-overlay">
                    <h3>{project.title}</h3>
                    <p>{project.category}</p>
                    <div className="project-tags">
                      {project.tags.slice(0, 2).map((tag, tagIndex) => (
                        <span key={tagIndex} className="tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {filteredProjects.length === 0 && (
            <div className="no-projects">
              <p>No projects found in this category.</p>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {selectedProject && (
        <div className="lightbox" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={closeLightbox}>×</button>
            <img src={selectedProject.image} alt={selectedProject.title} />
            <div className="lightbox-info">
              <h3>{selectedProject.title}</h3>
              <p>{selectedProject.description}</p>
              <div className="project-tags">
                {selectedProject.tags.map((tag, index) => (
                  <span key={index} className="tag">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Portfolio;